import { useEffect } from 'react';

import { OrdersModel, PaymentConfigModel, emailReceiptApi, ordersApi, paymentConfigApi, syncBillApi } from '@pukka/api';
import { RECEIPT_API } from '@pukka/common/constants';
import { assertUtil, dateUtils, numberUtils } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { formatDuration, intervalToDuration } from 'date-fns';
import upperFirst from 'lodash/upperFirst';
import words from 'lodash/words';
import { proxy } from 'valtio';

import { TransactionRefundState } from './Refund';
import { breakdownRefundedItems, sortOrderItems } from './Refund/refundUtils';
import { TAB } from './Transaction.state';
import { getItemNameWithBundleComponent } from './utils';

interface ITransactionDetailState {
  status: 'loading' | 'success' | 'error';
  details: [string, string | number][];
  items: [string, string | number][];
  voids: [string, string | number, string, string][];
  refunded: [string, string | number, string, string, string][];
  customer: [string, string | number, boolean][];
  id: string;
  token: string;
  resendReceiptStatus: 'loading' | 'success' | 'error';
  shouldShowResendReceiptModal: boolean;
  paymentId?: number;
  paymentUUID?: string;
  paymentMethods: PaymentConfigModel.PaymentMethod[];
  hasBeenReprinted: boolean;
  receiptUrl: string;
  readonly itemsLength: number;
  readonly voidsLength: number;
  readonly refundedLength: number;
}

const TransactionDetailState = proxy<ITransactionDetailState>({
  status: 'success',
  details: [],
  items: [],
  voids: [],
  refunded: [],
  customer: [],
  id: '',
  token: '',
  resendReceiptStatus: 'success',
  shouldShowResendReceiptModal: false,
  paymentMethods: [],
  hasBeenReprinted: false,
  receiptUrl: '',
  get itemsLength(): number {
    return TransactionDetailState.items.length;
  },
  get voidsLength(): number {
    return TransactionDetailState.voids.length;
  },
  get refundedLength(): number {
    return TransactionDetailState.refunded.length;
  },
});

function resetData() {
  TransactionDetailState.details = [];
  TransactionDetailState.items = [];
  TransactionDetailState.voids = [];
  TransactionDetailState.status = 'success';
}

function onToggleResendReceipt(show: boolean, State = TransactionDetailState) {
  State.shouldShowResendReceiptModal = show;
}

function onShowReceipt(State = TransactionDetailState) {
  if (State.receiptUrl) {
    window.open(State.receiptUrl);
  }
}

async function onResendReceipt(email: string, State = TransactionDetailState, getter = emailReceiptApi.sendEmailReceipt) {
  if (State.paymentId === undefined || State.paymentUUID === undefined || State.resendReceiptStatus === 'loading') {
    return;
  }

  State.resendReceiptStatus = 'loading';

  try {
    const response = await getter({
      customerEmail: email,
      resendEmail: true,
      paymentId: State.paymentId,
      paymentUuid: State.paymentUUID,
    });

    toast.success(response.message);

    State.resendReceiptStatus = 'success';
    State.shouldShowResendReceiptModal = false;
  } catch (error) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
    State.resendReceiptStatus = 'error';
  }
}

async function getSuccessTransactionDetail(
  token: string,
  State = TransactionDetailState,
  getter = ordersApi.getOrderDetail,
  paymentMethodsGetter = paymentConfigApi.getAllPaymentMethods,
) {
  if (State.token === token) {
    return false;
  }

  State.token = token;

  resetData();

  // Placeholder for loading
  State.details = [
    ['Order ID', ''],
    ['Receipt Number', ''],
    ['Completed Time', ''],
    ['[Table] / [Bill Name]', ''],
    ['Served by', ''],
    ['Pax', ''],
    ['Duration', ''],
    ['Collected by', ''],
    ['Total Amount', ''],
    ['Payment Method', ''],
  ];

  State.status = 'loading';

  try {
    const data = await getter(token);

    State.paymentId = data.id;
    State.paymentUUID = data.uuid;

    // The data is valid
    if (State.token === token) {
      const details: [string, string | number][] = [
        ['Order ID', data.order_id],
        ['Receipt Number', data.payment_no ?? ''],
        ['Completed Time', dateUtils.formatDate(data.created_at, 'dd MMM yyyy HH:mm')],
        [data.name ? 'Bill Name' : 'Table', data.name ?? data.table_name ?? ''],
        [
          'Served by',
          words(data.server_name ?? '')
            .map((w) => upperFirst(w))
            .join(' '),
        ],
        ['Pax', data.pax],
        [
          'Duration',
          data.bill_created_at &&
            data.created_at &&
            formatDuration(
              intervalToDuration({
                start: new Date(data.bill_created_at),
                end: new Date(data.created_at),
              }),
            ),
        ],
        [
          'Collected by',
          words(data.collector_name ?? '')
            .map((w) => upperFirst(w))
            .join(' '),
        ],
        ['Total Amount', numberUtils.formatCurrency(data.total_collected_amount, 'Rp')],
        [
          'Payment Method',
          data.payment_type === 'split_payment'
            ? data.split_payment_details.map((pd) => pd.payment_type_label).join(', ')
            : data.payment_type_label,
        ],
      ];

      if (data.total_refund) {
        details.push(['Total Refunded', numberUtils.formatCurrency(-data.total_refund, 'Rp')]);
      }

      State.details = details;
      State.items = data.items.map((i) => [getItemNameWithBundleComponent(i), i.quantity]);
      State.voids = data.void_items.map(
        (i) => [getItemNameWithBundleComponent(i), i.quantity, i.void_reason ?? '', i.void_by ?? ''] as [string, number, string, string],
      );
      State.refunded = breakdownRefundedItems(data.order_refunds);
      State.hasBeenReprinted = data.receipt_count > 0;
      State.receiptUrl = data.guid ? `${RECEIPT_API}${data.guid}/${data.payment_no}` : '';

      if (data.loyalty) {
        State.customer = [
          ['Member Since', dateUtils.formatDate(data.loyalty.member_since), false],
          ['Member Number', data.customer_email, false],
          ['Loyalty Program', ' ', false],
          ['Current Balances', numberUtils.formatCurrency(data.loyalty.current_balance ?? 0), true],
          ['New Earned Points', numberUtils.formatCurrency(data.loyalty.new_earned_points ?? 0), true],
          ['Redeemed in this purchase', numberUtils.formatCurrency(data.loyalty.redemption?.redeem_points ?? 0), true],
          ['Closing Balance', numberUtils.formatCurrency(data.loyalty.closing_balance ?? 0), true],
        ];
      } else {
        State.customer = [];
      }

      // set data for refund
      const sortedOrderItems = sortOrderItems(data.order_items as OrdersModel.OrderItems);
      const sortedOrderRefunds = data.order_refunds.map((refund) => ({
        ...refund,
        order_items: sortOrderItems(refund.order_items),
      }));

      TransactionRefundState.transactionDetail = {
        ...data,
        order_items: sortedOrderItems,
        order_refunds: sortedOrderRefunds,
      };
    }

    // fetch payment methods to determine whether the transaction is refundable
    if (State.paymentMethods.length === 0) {
      const paymentMethods = await paymentMethodsGetter();

      State.paymentMethods = paymentMethods;
    }

    State.status = 'success';
  } catch (error: unknown) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
    State.status = 'error';
  }

  return true;
}

async function getCancelledTransactionDetail(
  id: string,
  token: string,
  State = TransactionDetailState,
  getter = syncBillApi.getBillDetail,
) {
  if (State.id === id && State.token === token) {
    return false;
  }

  State.id = id;
  State.token = token;

  resetData();

  // Placeholder for loading
  State.details = [
    ['[Table] / [Bill Name]', ''],
    ['Time of Order', ''],
    ['Time of Cancellation', ''],
    ['Served by', ''],
    ['Cancelled by', ''],
    ['Order ID', ''],
  ];
  State.status = 'loading';

  try {
    const data = await getter(Number(id), Number(token));

    // The data is valid
    if (State.id === id && State.token === token) {
      State.details = [
        [data.name ? 'Bill Name' : 'Table', data.name ?? data.tableName ?? ''],
        ['Time of Order', dateUtils.formatDate(data.createdAt, 'dd MMM yyyy HH:mm')],
        ['Time of Cancellation', dateUtils.formatDate(data.cancelledAt, 'dd MMM yyyy HH:mm')],
        [
          'Served by',
          words(data.serveByName ?? '')
            .map((w) => upperFirst(w))
            .join(' '),
        ],
        [
          'Cancelled by',
          words(data.cancelledByName ?? '')
            .map((w) => upperFirst(w))
            .join(' '),
        ],
        ['Order ID', data.orderId ?? ''],
      ];

      State.items = data.orderItem?.map((i) => [getItemNameWithBundleComponent(i), i.itemCount]) ?? [];
      State.voids =
        data.cancelledItem?.map((i) => [
          getItemNameWithBundleComponent(i),
          i.itemCount,
          i.cancelledReason ?? '',
          i.cancelledByName ?? '',
        ]) ?? [];
      State.status = 'success';
    }
  } catch (error: unknown) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
    State.status = 'error';
  }

  return true;
}

function useTransactionDetail(
  tab: TAB,
  id?: string,
  token?: string,
  getSuccessDetail = getSuccessTransactionDetail,
  getCancelledDetail = getCancelledTransactionDetail,
) {
  useEffect(() => {
    if (!id) {
      return undefined;
    }

    switch (tab) {
      case 'cancelled':
        if (token) {
          getCancelledDetail(id, token);
        }

        break;
      case 'void':
        break;
      case 'success':
      default:
        getSuccessDetail(id);
        break;
    }

    return undefined;
  }, [tab, id, token, getCancelledDetail, getSuccessDetail]);
}

export {
  useTransactionDetail,
  resetData,
  onResendReceipt,
  onToggleResendReceipt,
  onShowReceipt,
  getSuccessTransactionDetail as _getSuccessTransactionDetail,
  getCancelledTransactionDetail as _getCancelledTransactionDetail,
  TransactionDetailState,
};
export type { ITransactionDetailState };
